import { LightbulbIcon, LightningIcon } from "@phosphor-icons/react";
import type {
  ColDef,
  ICellRendererParams,
  SortChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Buzzsumo from "@/assets/svg/Buzzsumo.svg?react";
import Readership from "@/assets/svg/Readership.svg?react";
import { TableButton } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import type { GetNarrativeClustersTableVariables } from "@/data-access/discovery-view/queries/discovery-view-queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { agGridTheme } from "@/lib/ag-grid-theme";
import { useFeature } from "@/lib/feature-management";
import { convertSortModelToStoryOrdering } from "@/lib/utils/narrativeClustersTableSortOrderingUtils";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import { useDiscoveryContext } from "@/store/discovery/discovery-params.context";
import NarrativeClusterCellRenderer from "./NarrativeClusterCellRenderer";
import {
  createNarrativeClustersUnifiedOverlay,
  type NarrativeClusterRowData,
} from "./NarrativeClustersTableStates";
import {
  createNarrativeClustersDatasource,
  startNewDatasourceSession,
} from "./narrativeClustersTableDataSource";

interface InnerHeaderWithIconProps {
  displayName?: string;
  icon?: React.ReactNode;
}

const InnerHeaderWithIcon = ({
  displayName,
  icon,
}: InnerHeaderWithIconProps) => (
  <div className="flex items-center gap-2">
    {icon && <span className="shrink-0">{icon}</span>}
    <span>{displayName}</span>
  </div>
);

const NarrativeClustersTable = () => {
  const gridRef = useRef<AgGridReact>(null);
  const hasInitializedSort = useRef(false);
  const isInitialMount = useRef(true);

  const isFetchingNarrativeClusters = useDiscoveryContentManagementStore(
    (state) => state.isFetchingNarrativeClustersTableData,
  );
  const error = useDiscoveryContentManagementStore(
    (state) => state.narrativeClustersTableError,
  );
  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalNarrativeClustersCount,
  );
  const isInitialLoading = useDiscoveryContentManagementStore(
    (state) => state.isNarrativeClustersInitialLoading,
  );
  const searchTerm = useDiscoveryContentManagementStore(
    (state) => state.narrativeClustersSearchTerm,
  );
  const setSearchTerm = useDiscoveryContentManagementStore(
    (state) => state.setNarrativeClustersSearchTerm,
  );
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const enableGenerateInsightColumn = useFeature("navigation-v2-exp", false);

  const { feedId, selectedFilters, startDate, endDate } = useDiscoveryContext();

  // Local state for ordering - default to TOTAL_MENTIONS descending
  const [ordering, setOrdering] = useState<
    GetNarrativeClustersTableVariables["ordering"]
  >([{ totalMentions: "DESC_NULLS_LAST" }]);

  const columnDefs = useMemo<ColDef<NarrativeClusterRowData>[]>(
    () => [
      ...(enableGenerateInsightColumn
        ? [
            {
              colId: "generateInsights",
              headerName: "Generate Insights",
              width: 200,
              minWidth: 200,
              pinned: "left" as const,
              lockPosition: true,
              resizable: false,
              sortable: false,
              cellRenderer: (params: ICellRendererParams) =>
                !params.data ? (
                  <div className="flex gap-2 items-center">
                    <Skeleton className="h-7 w-14 rounded-md" />
                    <Skeleton className="h-7 w-16 rounded-md" />
                  </div>
                ) : (
                  <div className="flex gap-2 items-center">
                    <TableButton
                      backgroundClassName="bg-gray-900"
                      size="sm"
                      className="rounded-md h-7"
                    >
                      <LightningIcon size={12} className="shrink-0" />
                      LLM
                    </TableButton>
                    <TableButton
                      backgroundClassName="bg-violet-950"
                      size="sm"
                      className="rounded-md h-7"
                    >
                      <LightbulbIcon size={12} className="shrink-0" />
                      Insight
                    </TableButton>
                  </div>
                ),
            } satisfies ColDef<NarrativeClusterRowData>,
          ]
        : []),
      {
        field: "TITLE",
        headerName: "Title",
        width: 300,
        minWidth: 200,
        resizable: true,
        sortable: false,
        wrapText: true,
        lockPosition: true,
        cellStyle: { display: "flex", alignItems: "flex-start" },
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => <NarrativeClusterCellRenderer params={params} wrapText={true} />,
      },
      {
        field: "TOTAL_MENTIONS",
        headerName: "Total Mentions",
        width: 160,
        minWidth: 160,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
            valueClassName="text-violet-800"
          />
        ),
      },
      {
        field: "NET_IMPACT_SCORE",
        headerName: "Net LLM Impact Score",
        width: 140,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
            valueClassName="text-violet-800"
          />
        ),
      },
      {
        field: "AVERAGE_ARTICLE_IMPACT",
        headerName: "Average Article Impact",
        width: 160,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
            valueClassName="text-violet-800"
          />
        ),
      },
      {
        field: "AVERAGE_IMPACT_STRENGTH",
        headerName: "Average Impact Strength",
        width: 160,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
            valueClassName="text-violet-800"
          />
        ),
      },
      {
        field: "READERSHIP",
        headerName: "Readership",
        width: 180,
        minWidth: 180,
        headerComponentParams: {
          innerHeaderComponent: InnerHeaderWithIcon,
          innerHeaderComponentParams: {
            displayName: "Readership",
            icon: <Readership height={14} />,
          },
        },
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "SOCIAL_ENGAGEMENTS",
        headerName: "Social Engagements",
        width: 220,
        minWidth: 200,
        headerComponentParams: {
          innerHeaderComponent: InnerHeaderWithIcon,
          innerHeaderComponentParams: {
            displayName: "Social Engagements",
            icon: <Buzzsumo height={14} />,
          },
        },
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "NET_SENTIMENT",
        headerName: "Net Sentiment",
        width: 120,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            showSentimentBadge={true}
          />
        ),
      },
      {
        field: "GROSS_IMPACT",
        headerName: "Gross Impact",
        width: 120,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "GROSS_POSITIVE_IMPACT",
        headerName: "Gross Positive Impact",
        width: 160,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
            valueClassName="text-green-700"
          />
        ),
      },
      {
        field: "GROSS_NEUTRAL_IMPACT",
        headerName: "Gross Neutral Impact",
        width: 160,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "GROSS_NEGATIVE_IMPACT",
        headerName: "Gross Negative Impact",
        width: 160,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
            valueClassName="text-red-750"
          />
        ),
      },
      {
        field: "TIER_1",
        headerName: "Tier 1",
        width: 90,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "TIER_2",
        headerName: "Tier 2",
        width: 90,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "TIER_3",
        headerName: "Tier 3",
        width: 90,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "HEADLINES",
        headerName: "Headlines",
        width: 100,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "FEATURES",
        headerName: "Features",
        width: 100,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
      {
        field: "PASSING_MENTIONS",
        headerName: "Passing Mentions",
        width: 130,
        cellRenderer: (
          params: ICellRendererParams<NarrativeClusterRowData>,
        ) => (
          <NarrativeClusterCellRenderer
            params={params}
            numberFormat="abbreviate"
          />
        ),
      },
    ],
    [enableGenerateInsightColumn],
  );

  const projectionFilters = useMemo<
    GetNarrativeClustersTableVariables["projectionFilters"]
  >(
    () => ({
      ...(feedId ? { feedId } : {}),
      ...(startDate && endDate ? { dateRange: { startDate, endDate } } : {}),
      ...(selectedFilters as GetNarrativeClustersTableVariables["projectionFilters"]),
    }),
    [feedId, selectedFilters, startDate, endDate],
  );

  const filters = useMemo<GetNarrativeClustersTableVariables["filters"]>(
    () => ({
      ...(feedId ? { feedId } : {}),
      ...(startDate && endDate ? { dateRange: { startDate, endDate } } : {}),
    }),
    [feedId, startDate, endDate],
  );

  const datasource = useMemo(() => {
    startNewDatasourceSession();
    return createNarrativeClustersDatasource(
      () => projectionFilters,
      () => filters,
      debouncedSearchTerm,
      ordering,
    );
  }, [projectionFilters, filters, debouncedSearchTerm, ordering]);

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.purgeInfiniteCache();
      gridRef.current.api.refreshInfiniteCache();
    }
  }, [datasource]);

  const handleRetry = useCallback(() => {
    gridRef.current?.api?.refreshInfiniteCache();
  }, []);

  const shouldShowOverlay =
    !isFetchingNarrativeClusters &&
    !error &&
    !isInitialLoading &&
    totalCount === 0;
  const shouldShowErrorOverlay = !isFetchingNarrativeClusters && !!error;

  const UnifiedOverlay = useMemo(
    () => createNarrativeClustersUnifiedOverlay(handleRetry),
    [handleRetry],
  );

  useEffect(() => {
    if (!gridRef.current?.api) return;
    if (shouldShowErrorOverlay || shouldShowOverlay) {
      gridRef.current.api.showNoRowsOverlay();
    } else {
      gridRef.current.api.hideOverlay();
    }
    if (shouldShowErrorOverlay) {
      gridRef.current.api.setRowCount(0, true);
    }
  }, [shouldShowOverlay, shouldShowErrorOverlay]);

  // Initialize AG Grid with default sort when grid is ready
  const handleGridReady = () => {
    if (!gridRef.current?.api || hasInitializedSort.current) return;

    gridRef.current.api.applyColumnState({
      state: [{ colId: "TOTAL_MENTIONS", sort: "desc" }],
      defaultState: { sort: null },
    });
    hasInitializedSort.current = true;
  };

  // Reset sorting to default when feedId changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (gridRef.current?.api) {
      gridRef.current.api.purgeInfiniteCache();
    }
    setOrdering([{ totalMentions: "DESC_NULLS_LAST" }]);
    setSearchTerm("");

    if (gridRef.current?.api) {
      gridRef.current.api.applyColumnState({
        state: [{ colId: "TOTAL_MENTIONS", sort: "desc" }],
        defaultState: { sort: null },
      });
    }
  }, [feedId]);

  const handleSortChanged = (event: SortChangedEvent) => {
    if (!event.api) return;
    const columnStates = event.api.getColumnState();
    const sortModel = columnStates
      .filter((col) => col.sort != null)
      .map((col) => ({
        colId: col.colId ?? "",
        sort: col.sort ?? "asc",
      }));
    const newOrdering = convertSortModelToStoryOrdering(sortModel);
    setOrdering(newOrdering);
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: TODO – replace <div> with <section> in a dedicated a11y PR */}
      <div
        className="ag-theme-quartz w-full h-full min-h-0 relative"
        aria-busy={isFetchingNarrativeClusters}
        aria-live="polite"
        aria-label={
          isFetchingNarrativeClusters ? "Loading narrative clusters" : undefined
        }
        style={
          {
            "--ag-grid-min-height": "100px",
          } as React.CSSProperties
        }
      >
        <AgGridReact<NarrativeClusterRowData>
          ref={gridRef}
          columnDefs={columnDefs}
          rowModelType="infinite"
          datasource={datasource}
          getRowId={(params) => params.data.id}
          defaultColDef={{
            sortable: true,
            resizable: true,
            cellStyle: { display: "flex", alignItems: "center" },
          }}
          rowSelection={{
            mode: "multiRow",
            checkboxes: false,
          }}
          theme={agGridTheme}
          rowHeight={60}
          headerHeight={36}
          cacheBlockSize={50}
          maxBlocksInCache={10}
          infiniteInitialRowCount={5}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[10, 20, 50, 100]}
          loadingOverlayComponent={() => null}
          noRowsOverlayComponent={UnifiedOverlay}
          onGridReady={handleGridReady}
          onSortChanged={handleSortChanged}
        />
      </div>
    </>
  );
};

export default NarrativeClustersTable;
